import { forwardRef, useRef } from 'react'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Plus, Trash2 } from 'lucide-react'
import PlayerTypeahead from '../PlayerTypeahead'

const MatchCard = forwardRef(function MatchCard({
  matchNumber,
  round = 1,
  player1,
  player1Seed,
  player2,
  player2Seed,
  player1PQRef,
  player2PQRef,
  nextRound,
  nextMatchNumber,
  onPlayer1Change,
  onPlayer1SeedChange,
  onPlayer2Change,
  onPlayer2SeedChange,
  onDelete,
  onInsertBelow = null, // If provided, renders a "+ Insert match below" button (round 1 only)
  onAddPQ,
  showDelete = true, // Control delete button visibility
  divisionGender = null, // Division gender for player creation
  divisionTeamSize = 1, // Division team size for player creation
  sportId = null, // Sport to auto-assign when creating players from bracket context
  // Team support: player1/player2 can be arrays for teams
  onPlayer1TeamChange, // (index, value) => void
  onPlayer2TeamChange,  // (index, value) => void
  validationErrors = {}, // Validation errors object
  matchKey = '', // Key for this match in validation errors
  excludePlayerIds = [], // Array of player IDs already used in bracket
  feedsIntoSlot = null, // Which slot this PQ feeds into (1 or 2)
  parentMatchSeedTabIndex = null // Tab index for parent match seed field (for PQ matches)
}, ref) {
  const p2SeedRef = useRef(null)
  const isPQ = round === 0
  const hasPlayer1PQ = !!player1PQRef
  const hasPlayer2PQ = !!player2PQRef
  const isTeam = divisionTeamSize > 1
  
  // Determine badge variant based on slot
  let matchBadgeVariant = 'blue'
  let pq1BadgeVariant = 'teal'
  let pq2BadgeVariant = 'orange'
  if (isPQ) {
    if (feedsIntoSlot === 1) matchBadgeVariant = 'teal'
    else if (feedsIntoSlot === 2) matchBadgeVariant = 'orange'
  }
  
  const matchLabel = isPQ 
    ? `Pre-Qualifier Match ${matchNumber}`
    : `Round ${round} Match ${matchNumber}`
  
  const nextMatchLabel = nextRound && nextMatchNumber
    ? `Round ${nextRound} Match ${nextMatchNumber}`
    : null
  
  // Helper to render team member slots
  const renderTeamSlots = (team, onTeamChange, playerPosition, validationKey, hasError, onClearSeed, seedValue, onClearBothTeams = null, seedRef = null) => {
    const isMixedDoubles = divisionTeamSize === 2 && divisionGender === 'mixed'
    
    const teamMemberInputs = []
    for (let i = 0; i < divisionTeamSize; i++) {
      const member = Array.isArray(team) ? team[i] : (i === 0 ? team : null)
      
      // Determine label based on division type
      let label
      if (isMixedDoubles) {
        label = i === 0 ? 'Female Player' : 'Male Player'
      } else {
        label = i === 0 ? 'Player One' : 'Player Two'
      }
      
      teamMemberInputs.push(
        <div key={i}>
          <label className="text-xs text-muted-foreground mb-1 block">
            {label}
          </label>
          <PlayerTypeahead
            value={member}
            onChange={(value) => onTeamChange(i, value)}
            placeholder="Enter player name..."
            divisionGender={divisionGender}
            divisionTeamSize={divisionTeamSize}
            playerPosition={playerPosition + i}
            excludePlayerIds={excludePlayerIds}
            validationKey={validationKey}
            error={hasError}
            tabIndex={0}
            sportId={sportId}
          />
        </div>
      )
    }
    
    // Check if any team member has a value or if seed has a value
    const hasTeamMembers = Array.isArray(team) ? team.some(member => member) : !!team
    const shouldShowClear = hasTeamMembers || seedValue
    
    // Return a flex container with vertical layout for team members and optional clear button
    return (
      <>
        <div className="flex-1 flex flex-col gap-3">
          {teamMemberInputs}
        </div>
        {shouldShowClear && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              
              // If onClearBothTeams is provided (doubles), use it to clear everything
              if (onClearBothTeams) {
                onClearBothTeams()
              } else {
                // Otherwise just clear this team (singles)
                for (let i = 0; i < divisionTeamSize; i++) {
                  onTeamChange(i, null)
                }
                onClearSeed()
              }
              // Focus seed field after React re-renders
              // Use querySelector because seedRef may be a callback ref (no .current)
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  const seedKey = validationKey?.replace(/-player$/, '-seed')
                  const seedInput = seedKey && document.querySelector(`[data-validation-key="${seedKey}"]`)
                  if (seedInput) {
                    seedInput.focus()
                  } else if (seedRef?.current) {
                    seedRef.current.focus()
                  }
                })
              })
            }}
            onKeyDown={(e) => {
              // For PQ matches, intercept Tab key to focus parent match PQ button
              if (isPQ && parentMatchSeedTabIndex && e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault()
                const parentMatchSelector = `[data-parent-pq-button="${parentMatchSeedTabIndex}"]`
                const parentPQButton = document.querySelector(parentMatchSelector)
                if (parentPQButton) {
                  parentPQButton.focus()
                }
              }
            }}
            className="px-3 mt-5 h-10"
            title="Clear team and seed"
            tabIndex={-1}
          >
            Clear
          </Button>
        )}
      </>
    )
  }
  
  return (
    <div className="border rounded-lg p-4 bg-background hover:shadow-md transition-shadow relative" data-match-card>
      {/* Match Label Pills - Current and Next */}
      <div className="absolute top-3 left-3 flex items-center gap-2">
        <Badge variant={matchBadgeVariant} className="text-xs">
          {matchLabel}
        </Badge>
        {nextMatchLabel && (
          <Badge variant="outline" className="text-xs">
            → {nextMatchLabel}
          </Badge>
        )}
      </div>
      
      {/* Top-right action buttons: insert-below + delete.
          DUB-55: the button now appears on whichever card ends a R1+PQ group —
          the R1 when it has no PQs, or the last PQ when the R1 has them — so
          the inserted match lands AFTER the group instead of splitting it. */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        {onInsertBelow && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onInsertBelow}
            tabIndex={-1}
            title="Insert a new match below this one"
            aria-label="Insert match below"
            className="h-8 gap-1 px-2 text-xs text-muted-foreground hover:text-primary"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Insert below</span>
          </Button>
        )}
        {showDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            tabIndex={-1}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      <div className="mt-8 space-y-3">
        {/* Player 1 with Seed and PQ button OR PQ Reference */}
        <div>
          {hasPlayer1PQ ? (
            // Show PQ reference instead of inputs
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Player One</label>
              <div className="flex items-center gap-2 h-10 px-3 py-2 border rounded-md bg-muted/50">
                <Badge variant={pq1BadgeVariant} className="text-sm">
                  {player1PQRef}
                </Badge>
                <span className="text-sm text-muted-foreground">Winner of Pre-Qualifier</span>
              </div>
            </div>
          ) : (
            // Show normal inputs with PQ button (only for non-PQ matches)
            <div className="flex gap-2">
              {!isPQ && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAddPQ(1)}
                  className="px-3 mt-5"
                  title="Add Pre-Qualifier Match for Player One"
                  tabIndex={0}
                  data-parent-pq-button={`${1000 + (matchNumber * 10) + 1}`}
                >
                  PQ
                </Button>
              )}
              <div className="w-16">
                <label className="text-xs text-muted-foreground mb-1 block">Seed</label>
                <Input
                  ref={ref}
                  value={player1Seed}
                  onChange={(e) => onPlayer1SeedChange(e.target.value)}
                  placeholder="#"
                  maxLength={3}
                  className="w-16 text-center"
                  error={!!validationErrors[`${matchKey}-p1-seed`]}
                  title={validationErrors[`${matchKey}-p1-seed`] || ''}
                  data-validation-key={`${matchKey}-p1-seed`}
                  tabIndex={hasPlayer1PQ ? -1 : 0}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && !e.shiftKey) {
                      // Skip PQ button, go directly to player name input
                      const nameInput = e.currentTarget.closest('.flex')?.querySelector(`[data-validation-key="${matchKey}-p1-player"]`)
                      if (nameInput) {
                        e.preventDefault()
                        nameInput.focus()
                      }
                    }
                  }}
                />
              </div>
              {isTeam ? (
                renderTeamSlots(
                  player1,
                  onPlayer1TeamChange,
                  1,
                  `${matchKey}-p1-player`,
                  !!validationErrors[`${matchKey}-p1-player`],
                  () => onPlayer1SeedChange(''),
                  player1Seed,
                  () => {
                    // Clear only Player 1's team and seed
                    onPlayer1Change(null)
                    onPlayer1SeedChange('')
                  },
                  ref
                )
              ) : (
                <>
                  <div className="flex-1">
                    <label className="text-xs text-muted-foreground mb-1 block">Player One</label>
                    <PlayerTypeahead
                      value={player1}
                      onChange={onPlayer1Change}
                      placeholder="Enter player name..."
                      divisionGender={divisionGender}
                      divisionTeamSize={divisionTeamSize}
                      playerPosition={1}
                      excludePlayerIds={excludePlayerIds}
                      error={!!validationErrors[`${matchKey}-p1-player`]}
                      validationKey={`${matchKey}-p1-player`}
                      tabIndex={0}
                      sportId={sportId}
                      onFocusNext={() => {
                        // For non-PQ matches, focus P2's PQ button first so admin
                        // can decide if they want a pre-qualifier before entering P2.
                        // Scope to current match card to avoid hitting same-numbered
                        // matches in other rounds.
                        if (!isPQ) {
                          const currentCard = document.querySelector(`[data-validation-key="${matchKey}-p1-seed"]`)?.closest('[data-match-card]')
                          const pqButton = currentCard?.querySelector(`[data-parent-pq-button="${1000 + (matchNumber * 10) + 2}"]`)
                          if (pqButton) { pqButton.focus(); return }
                        }
                        // Fallback: focus P2 seed directly
                        const p2Seed = document.querySelector(`[data-validation-key="${matchKey}-p2-seed"]`)
                        if (p2Seed) { p2Seed.focus(); return }
                        p2SeedRef.current?.focus()
                      }}
                    />
                  </div>
                  {(player1 || player1Seed) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onPlayer1Change(null)
                        onPlayer1SeedChange('')
                        // Double rAF ensures React has re-rendered after state clear
                        requestAnimationFrame(() => {
                          requestAnimationFrame(() => {
                            const seedInput = document.querySelector(`[data-validation-key="${matchKey}-p1-seed"]`)
                            if (seedInput) {
                              seedInput.focus()
                            } else if (ref?.current) {
                              ref.current.focus()
                            }
                          })
                        })
                      }}
                      className="px-3 mt-5 h-10"
                      title="Clear player and seed"
                      tabIndex={-1}
                    >
                      Clear
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Player 2 with Seed and PQ button OR PQ Reference */}
        <div>
          {hasPlayer2PQ ? (
            // Show PQ reference instead of inputs
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Player Two</label>
              <div className="flex items-center gap-2 h-10 px-3 py-2 border rounded-md bg-muted/50">
                <Badge variant={pq2BadgeVariant} className="text-sm">
                  {player2PQRef}
                </Badge>
                <span className="text-sm text-muted-foreground">Winner of Pre-Qualifier</span>
              </div>
            </div>
          ) : (
            // Show normal inputs with PQ button (only for non-PQ matches)
            <div className="flex gap-2">
              {!isPQ && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAddPQ(2)}
                  className="px-3 mt-5"
                  title="Add Pre-Qualifier Match for Player Two"
                  tabIndex={0}
                  data-parent-pq-button={`${1000 + (matchNumber * 10) + 2}`}
                >
                  PQ
                </Button>
              )}
              <div className="w-16">
                <label className="text-xs text-muted-foreground mb-1 block">Seed</label>
                <Input
                  ref={p2SeedRef}
                  value={player2Seed}
                  onChange={(e) => onPlayer2SeedChange(e.target.value)}
                  placeholder="#"
                  maxLength={3}
                  className="w-16 text-center"
                  error={!!validationErrors[`${matchKey}-p2-seed`]}
                  title={validationErrors[`${matchKey}-p2-seed`] || ''}
                  data-validation-key={`${matchKey}-p2-seed`}
                  tabIndex={hasPlayer2PQ ? -1 : 0}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && !e.shiftKey) {
                      // Skip PQ button, go directly to player name input
                      const nameInput = e.currentTarget.closest('.flex')?.querySelector(`[data-validation-key="${matchKey}-p2-player"]`)
                      if (nameInput) {
                        e.preventDefault()
                        nameInput.focus()
                      }
                    }
                  }}
                />
              </div>
              {isTeam ? (
                renderTeamSlots(
                  player2,
                  onPlayer2TeamChange,
                  1,
                  `${matchKey}-p2-player`,
                  !!validationErrors[`${matchKey}-p2-player`],
                  () => onPlayer2SeedChange(''),
                  player2Seed,
                  () => {
                    // Clear only Player 2's team and seed
                    onPlayer2Change(null)
                    onPlayer2SeedChange('')
                  },
                  p2SeedRef
                )
              ) : (
                <>
                  <div className="flex-1">
                    <label className="text-xs text-muted-foreground mb-1 block">Player Two</label>
                    <PlayerTypeahead
                      value={player2}
                      onChange={onPlayer2Change}
                      placeholder="Enter player name..."
                      divisionGender={divisionGender}
                      divisionTeamSize={divisionTeamSize}
                      playerPosition={2}
                      excludePlayerIds={excludePlayerIds}
                      error={!!validationErrors[`${matchKey}-p2-player`]}
                      sportId={sportId}
                      validationKey={`${matchKey}-p2-player`}
                      tabIndex={0}
                      onFocusNext={() => {
                        // Collect ALL match containers in DOM order and find ours by index.
                        // This is more reliable than nextElementSibling which can skip or
                        // hit unexpected elements when PQ matches are interspersed.
                        const allContainers = Array.from(document.querySelectorAll('[data-match-container]'))
                        const seedEl = document.querySelector(`[data-validation-key="${matchKey}-p2-seed"]`)
                        const currentContainer = seedEl?.closest('[data-match-container]')
                        const currentIndex = currentContainer ? allContainers.indexOf(currentContainer) : -1

                        if (currentIndex >= 0 && currentIndex < allContainers.length - 1) {
                          const nextContainer = allContainers[currentIndex + 1]
                          // Prefer PQ button, then seed input in the next match
                          const nextPQ = nextContainer.querySelector('[data-parent-pq-button]')
                          if (nextPQ) { nextPQ.focus(); return }
                          const nextSeed = nextContainer.querySelector('[data-validation-key$="-p1-seed"]')
                          if (nextSeed) { nextSeed.focus(); return }
                        }
                        // Fallback: focus the "Add Another Match" button
                        const addBtn = document.querySelector('[data-add-match-button]')
                        if (addBtn) addBtn.focus()
                      }}
                    />
                  </div>
                  {(player2 || player2Seed) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onPlayer2Change(null)
                        onPlayer2SeedChange('')
                        setTimeout(() => p2SeedRef.current?.focus(), 0)
                      }}
                      onKeyDown={(e) => {
                        // For PQ matches, intercept Tab key to focus parent match PQ button
                        if (isPQ && parentMatchSeedTabIndex && e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault()
                          const parentMatchSelector = `[data-parent-pq-button="${parentMatchSeedTabIndex}"]`
                          const parentPQButton = document.querySelector(parentMatchSelector)
                          if (parentPQButton) {
                            parentPQButton.focus()
                          }
                        }
                      }}
                      className="px-3 mt-5 h-10"
                      title="Clear player and seed"
                      tabIndex={-1}
                    >
                      Clear
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

export default MatchCard
